import { isLooselyEqual, normalizeWhitespace } from '@attain-apps/string-utils'
import { TypeChecker } from '@attain-apps/type-guards'

/**
 * One row read from a worksheet.
 *
 * Each array index is the zero-based worksheet column position. Cell values
 * stay `unknown` because a spreadsheet reader can produce strings, numbers,
 * booleans, dates, or nulls before a domain parser normalizes the columns it
 * cares about.
 */
export type SheetRow = readonly unknown[]
export namespace SheetRow {
  export const isSheetRow: TypeChecker<SheetRow> =
    TypeChecker.isReadonlyArray.usingAlias('SheetRow')

  /**
   * A worksheet split around its column header row, with the positions of the
   * columns a parser asked for.
   */
  export interface Table<TColumn extends string> {
    /** Rows above the header, such as a report title or section labels. */
    readonly preamble: readonly SheetRow[]
    readonly header: SheetRow
    /** Rows below the header. */
    readonly body: readonly SheetRow[]
    readonly columns: Readonly<Record<TColumn, number>>
  }

  /**
   * Finds the header row containing every requested column and splits the
   * sheet around it.
   *
   * Each column is given by the header names it may appear under, matched
   * loosely because source workbooks are maintained by hand.
   *
   * @example
   * ```ts
   * const { body, columns } = SheetRow.parseTable(rows, {
   *   projectId: ['ProjectID'],
   *   budRef: ['Bud_ref'],
   * })
   * body.map(row => row[columns.projectId])
   * ```
   */
  export function parseTable<TColumn extends string>(
    rows: readonly SheetRow[],
    headerNames: Readonly<Record<TColumn, readonly string[]>>,
  ): Table<TColumn> {
    const keys = Object.keys(headerNames) as TColumn[]
    const headerIndex = rows.findIndex(row =>
      keys.every(
        key => findOptionalColumn(row, headerNames[key]) !== undefined,
      ),
    )
    if (headerIndex === -1) {
      const expected = keys.map(key => headerNames[key].join(' / '))
      throw new Error(
        `Sheet has no header row with all of: ${expected.join(', ')}`,
      )
    }

    const header = rows[headerIndex] ?? []
    const columns = Object.fromEntries(
      keys.map(key => [key, findColumn(header, headerNames[key])]),
    ) as Record<TColumn, number>
    return {
      body: rows.slice(headerIndex + 1),
      columns,
      header,
      preamble: rows.slice(0, headerIndex),
    }
  }

  /**
   * Finds a required column by any of the header names it may appear under.
   */
  export function findColumn(
    header: SheetRow,
    names: readonly string[],
  ): number {
    const column = findOptionalColumn(header, names)
    if (column === undefined) {
      throw new Error(`Sheet is missing column: ${names.join(' / ')}`)
    }
    return column
  }

  /**
   * Finds an optional column by any of the header names it may appear under.
   */
  export function findOptionalColumn(
    header: SheetRow,
    names: readonly string[],
  ): number | undefined {
    const column = header.findIndex(cell => {
      const text = cellText(cell)
      return (
        text !== undefined && names.some(name => isLooselyEqual(text, name))
      )
    })
    return column === -1 ? undefined : column
  }

  /**
   * A cell as trimmed, single-spaced text, or `undefined` when it is blank or
   * not a primitive value. Dates become `YYYY-MM-DD`.
   */
  export function cellText(value: unknown): string | undefined {
    const text = _toString(value)
    return text === undefined
      ? undefined
      : normalizeWhitespace(text) || undefined
  }

  /**
   * A cell as a number, or `undefined` when it is blank or not numeric.
   *
   * Accepts the forms accounting exports use: currency symbols, thousands
   * separators, and negative amounts written in parentheses (`(123.45)`).
   */
  export function parseOptionalNumber(value: unknown): number | undefined {
    if (typeof value === 'number') {
      return value
    }

    const compact = cellText(value)?.replace(/[$,\s]/g, '')
    if (!compact || compact === '-') {
      return undefined
    }

    const isNegative = compact.startsWith('(') && compact.endsWith(')')
    const parsed = Number(compact.replace(/[()]/g, ''))
    if (!Number.isFinite(parsed)) {
      return undefined
    }
    return isNegative ? -parsed : parsed
  }

  /**
   * A cell as an ISO date (`YYYY-MM-DD`), or `undefined` when it is blank.
   *
   * Accepts a `Date`, an Excel serial number, or `M/D/YY` / `M/D/YYYY` text;
   * any other text is returned as it is.
   */
  export function cellDate(value: unknown): string | undefined {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString().slice(0, 10)
    }

    if (typeof value === 'number') {
      // Excel serial dates use 1899-12-30 as the JavaScript-compatible epoch.
      return new Date(Date.UTC(1899, 11, 30 + value)).toISOString().slice(0, 10)
    }

    const text = cellText(value)
    if (!text) {
      return undefined
    }

    const match = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(text)
    if (!match) {
      return text
    }

    const month = Number(match[1])
    const day = Number(match[2])
    const rawYear = Number(match[3])
    const year =
      rawYear < 100 ? (rawYear < 50 ? 2000 + rawYear : 1900 + rawYear) : rawYear
    return [
      String(year).padStart(4, '0'),
      String(month).padStart(2, '0'),
      String(day).padStart(2, '0'),
    ].join('-')
  }

  /**
   * Parses the cell in `column`, or `undefined` when the column is missing,
   * for optional columns found with {@link findOptionalColumn}.
   */
  export function valueAt<T>(
    row: SheetRow,
    column: number | undefined,
    parse: (value: unknown) => T | undefined,
  ): T | undefined {
    return column === undefined ? undefined : parse(row[column])
  }

  /**
   * A row's cells keyed by their header, kept on parsed rows for diagnostics.
   * A repeated header keeps every value, with a numeric suffix (`OH 2`).
   */
  export function toRecord(
    header: SheetRow,
    row: SheetRow,
  ): Record<string, unknown> {
    const record: Record<string, unknown> = {}
    const counts = new Map<string, number>()
    for (const [index, headerCell] of header.entries()) {
      const key = cellText(headerCell)
      if (!key) {
        continue
      }
      const count = counts.get(key) ?? 0
      counts.set(key, count + 1)
      record[count === 0 ? key : `${key} ${count + 1}`] = row[index] ?? null
    }
    return record
  }

  export const isRecord: TypeChecker<Record<string, unknown>> =
    TypeChecker.Factory.record(TypeChecker.isString, TypeChecker.isUnknown)
}

/**
 * Converts primitive cell values to a string without stringifying objects.
 */
function _toString(value: unknown): string | undefined {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? undefined
      : value.toISOString().slice(0, 10)
  }
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value)
  }
  return undefined
}
